import React, { useState, useEffect, useRef } from "react";
import { getApi } from "../../../Services/apiService";
import {
  sendMessage,
  uploadChatImage,
  updateMessage,
  markMessagesAsRead,
  getOtherParticipantId,
  updateParticipantInfo,
  softDeleteMessage,
  blockUserInChat,
  unblockUserInChat,
  getIndividualChatBlocksMap,
  resolveMyParticipantIdInChat,
  blockMemberInGroupChat,
  unblockMemberInGroupChat,
  leaveGroupChat,
  addSystemMessage,
  subscribeToMessages,
} from "../../../Services/messagingService";
import { renderColorDotAndName } from "../../../Utils/colors";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import ReactModal from "react-modal";
import { getFamilyTreeIdFromStorage, getAdminDetailsFromStorage, getAdminUserIdFromDetails } from "../../../Utils/headOfLineRole";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import { getChatAttachmentType, getChatFileExtension } from "../../../Utils/messageFormatting";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons";
import MessageComposer from "./MessageComposer";
import MessageText from "./MessageText";
import MessageAttachment from "./MessageAttachment";

const ChatView = ({
  chat,
  currentUserId,
  onBack,
  chatListCollapsed,
  onExpandChatList,
}) => {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [messageMenuOpenId, setMessageMenuOpenId] = useState(null);
  const [messageSearchQuery, setMessageSearchQuery] = useState("");
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
  const [showBlockMemberModal, setShowBlockMemberModal] = useState(false);
  const [blockMemberSearchQuery, setBlockMemberSearchQuery] = useState("");
  const [showUnblockMemberModal, setShowUnblockMemberModal] = useState(false);
  const [unblockMemberSearchQuery, setUnblockMemberSearchQuery] = useState("");
  const [leaving, setLeaving] = useState(false);
  const [deletingMessages, setDeletingMessages] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch member information for individual chats
  useEffect(() => {
    const fetchMemberInfo = async () => {
      if (!chat || chat.type === "group") {
        setMemberInfo(null);
        return;
      }

      const otherUserId = getOtherParticipantId(chat, currentUserId);
      if (!otherUserId) return;

      // First, check if participant info is stored in chat document
      if (chat.participantInfo && chat.participantInfo[otherUserId]) {
        console.log("Using participant info from chat document:", chat.participantInfo[otherUserId]);
        setMemberInfo(chat.participantInfo[otherUserId]);
      } else {
        // If not in chat document, try to fetch from API
        try {
          const familyTreeId = getFamilyTreeIdFromStorage();

          if (!familyTreeId) return;

          const response = await getApi(
            `/family-members-for-invitation?family_tree_id=${familyTreeId}`
          );

          if (response?.statusCode === 200 && response?.data?.members) {
            const member = response.data.members.find(
              (m) => m.user_id === otherUserId
            );
            if (member) {
              console.log("Found member info for ChatView:", member);
              setMemberInfo(member);
            } else {
              console.log("Member not found in API response. Looking for user_id:", otherUserId);
              console.log("Available user_ids:", response.data.members.map(m => m.user_id));
            }
          }
        } catch (error) {
          console.error("Error fetching member info:", error);
        }
      }

      // Also update current user's participant info if missing
      if (chat.id && currentUserId) {
        try {
          const familyTreeId = getFamilyTreeIdFromStorage();

          if (familyTreeId) {
            const response = await getApi(
              `/family-members-for-invitation?family_tree_id=${familyTreeId}`
            );

            if (response?.statusCode === 200 && response?.data?.members) {
              const currentUserMember = response.data.members.find(
                (m) => m.user_id === currentUserId
              );
              if (currentUserMember) {
                // Update chat document with current user's info if missing
                await updateParticipantInfo(chat.id, currentUserId, currentUserMember);
              }
            }
          }
        } catch (error) {
          console.error("Error updating current user's participant info:", error);
        }
      }
    };

    fetchMemberInfo();
  }, [chat, currentUserId]);

  // Fetch group members information for group chats
  useEffect(() => {
    const fetchGroupMembers = async () => {
      if (!chat || chat.type !== "group" || !chat.participants) {
        setGroupMembers([]);
        return;
      }

      try {
        const familyTreeId = getFamilyTreeIdFromStorage();

        if (!familyTreeId) return;

        const response = await getApi(
          `/family-members-for-invitation?family_tree_id=${familyTreeId}`
        );

        if (response?.statusCode === 200 && response?.data?.members) {
          // Get member info for all participants except current user
          const members = chat.participants
            .filter((userId) => userId !== currentUserId)
            .map((userId) => {
              return response.data.members.find((m) => m.user_id === userId);
            })
            .filter((m) => m !== undefined); // Remove undefined entries

          setGroupMembers(members);
        }
      } catch (error) {
        console.error("Error fetching group members:", error);
      }
    };

    fetchGroupMembers();
  }, [chat, currentUserId]);

  // Subscribe to messages
  useEffect(() => {
    if (!chat?.id || !currentUserId) return;

    setLoading(true);
    const unsubscribe = subscribeToMessages(chat.id, (messagesList) => {
      setMessages(messagesList);
      setLoading(false);

      // Mark messages as read when viewing
      setTimeout(() => {
        markMessagesAsRead(chat.id, currentUserId);
      }, 500);

      // Scroll to bottom after messages load
      setTimeout(scrollToBottom, 100);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [chat?.id, currentUserId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle send message (new or edit)
  const getSenderName = () => {
    const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
    let senderName = localUser?.first_name || localUser?.name || "You";
    const suffix = localUser?.suffix || "";
    const specialName = localUser?.special_name || "";
    if (suffix) {
      const suffixUpper = suffix.trim().toUpperCase();
      const suffixWithPeriod =
        suffixUpper === "SR" || suffixUpper === "JR"
          ? `${suffix.trim()}.`
          : suffix.trim();
      senderName = `${senderName}, ${suffixWithPeriod}`;
    } else if (specialName) {
      senderName = `${senderName} ${specialName}`;
    }
    return senderName;
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending) return;

    setSending(true);
    try {
      if (editingMessageId) {
        await updateMessage(chat.id, editingMessageId, messageText.trim());
        setMessageText("");
        setEditingMessageId(null);
      } else {
        await sendMessage(
          chat.id,
          currentUserId,
          messageText.trim(),
          getSenderName()
        );
        setMessageText("");
      }
      setMessageMenuOpenId(null);
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!chat?.id || !currentUserId || uploadingFile) return;

    setUploadingFile(true);
    try {
      const fileUrl = await uploadChatImage(file);
      const messageType = getChatAttachmentType(file);
      await sendMessage(
        chat.id,
        currentUserId,
        "",
        getSenderName(),
        messageType,
        {
          fileUrl,
          fileName: file.name,
          mimeType: file.type || null,
          fileExtension: getChatFileExtension(file.name, file.type),
        }
      );
      toast.success(messageType === "image" ? "Photo sent." : "File sent.");
    } finally {
      setUploadingFile(false);
    }
  };

  // Start editing: put message text in input and focus
  const handleEditMessage = (message) => {
    setMessageText(message.text || "");
    setEditingMessageId(message.id);
    setMessageMenuOpenId(null);
    setTimeout(() => messageInputRef.current?.focus(), 50);
  };

  const meInChatId = chat ? resolveMyParticipantIdInChat(chat, currentUserId) : currentUserId;
  const otherUserId = chat ? getOtherParticipantId(chat, meInChatId) : null;
  const isOneToOneChat =
    !!otherUserId &&
    (chat?.type === "individual" ||
      (chat?.type !== "group" && (chat?.participants || []).length === 2));
  const isGroupCreator = chat?.type === "group" && chat?.createdBy === currentUserId;
  const individualBlocks = isOneToOneChat ? getIndividualChatBlocksMap(chat) : {};
  const blockWhoBlockedVictim = (blocks, victimId) => {
    if (!victimId || !blocks) return null;
    const key = Object.keys(blocks).find((k) => String(k) === String(victimId));
    return key != null ? blocks[key] : null;
  };
  const myBlockerUserId = blockWhoBlockedVictim(individualBlocks, meInChatId);
  const isBlocked =
    (isOneToOneChat && !!myBlockerUserId) ||
    (chat?.type === "group" && (chat?.blockedMembers || []).includes(currentUserId));
  const iBlockedOtherUser =
    isOneToOneChat &&
    otherUserId &&
    String(blockWhoBlockedVictim(individualBlocks, otherUserId) || "") === String(meInChatId);

  const handleToggleSelectMessage = (messageId) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const handleDeleteSelectedMessages = async () => {
    if (selectedMessageIds.size === 0) return;
    setDeletingMessages(true);
    try {
      for (const id of selectedMessageIds) {
        await softDeleteMessage(chat.id, id, currentUserId);
      }
      setSelectedMessageIds(new Set());
      setDeleteMode(false);
      setHeaderMenuOpen(false);
      toast.success("Messages deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete messages.");
    } finally {
      setDeletingMessages(false);
    }
  };

  const handleDeleteSingleMessage = async (messageId) => {
    setMessageMenuOpenId(null);
    try {
      await softDeleteMessage(chat.id, messageId, currentUserId);
      toast.success("Message deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete message.");
    }
  };

  const handleDeleteAllMessages = async () => {
    const deletable = messages.filter(
      (m) => m.senderId === currentUserId && !m.deleted && m.type !== "system"
    );
    setHeaderMenuOpen(false);
    if (deletable.length === 0) {
      toast.info("No messages to delete.");
      return;
    }
    if (
      !window.confirm(
        `Delete all ${deletable.length} of your messages in this chat? This can't be undone.`
      )
    ) {
      return;
    }
    setDeletingMessages(true);
    try {
      for (const m of deletable) {
        await softDeleteMessage(chat.id, m.id, currentUserId);
      }
      setSelectedMessageIds(new Set());
      setDeleteMode(false);
      toast.success("All messages deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete messages.");
    } finally {
      setDeletingMessages(false);
    }
  };

  const handleBlockUser = async () => {
    if (!otherUserId) return;
    setHeaderMenuOpen(false);
    try {
      await blockUserInChat(chat.id, meInChatId, otherUserId);
      toast.success("User blocked.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block user.");
    }
  };

  const handleUnblockUser = async () => {
    setHeaderMenuOpen(false);
    try {
      await unblockUserInChat(chat.id, meInChatId, otherUserId);
      toast.success("User unblocked.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to unblock user.");
    }
  };

  const handleBlockMember = async (memberUserId) => {
    setHeaderMenuOpen(false);
    try {
      await blockMemberInGroupChat(chat.id, memberUserId);
      toast.success("Member blocked.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block member.");
    }
  };

  const handleUnblockMember = async (memberUserId) => {
    try {
      await unblockMemberInGroupChat(chat.id, memberUserId);
      toast.success("Member unblocked.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to unblock member.");
    }
  };

  const handleLeaveGroup = async () => {
    setHeaderMenuOpen(false);
    setLeaving(true);
    try {
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const userName = formatMemberName(localUser) || "A member";
      await leaveGroupChat(chat.id, currentUserId, userName);
      toast.success("You left the group.");
      if (onBack) onBack();
    } catch (err) {
      console.error(err);
      toast.error("Failed to leave group.");
    } finally {
      setLeaving(false);
    }
  };

  // Format message time
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = timestamp.toDate();
      const now = new Date();
      const isToday =
        date.toDateString() === now.toDateString();

      if (isToday) {
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (error) {
      return "";
    }
  };

  // Get participant name
  const getParticipantName = () => {
    if (chat.type === "group") {
      return chat.groupName || "Group Chat";
    }

    const otherUserId = getOtherParticipantId(chat, currentUserId);
    if (!otherUserId) return "Unknown";

    // Admin Support chat: user sees "Admin Support (AdminName)", admin sees just the user's name
    try {
      const adminDetails = getAdminDetailsFromStorage();
      const adminId = getAdminUserIdFromDetails(adminDetails);
      const otherIsAdmin = adminId && String(adminId) === String(otherUserId);
      const isAdminSupportChat = otherIsAdmin || chat.isAdminSupport === true;
      if (isAdminSupportChat) {
        if (otherIsAdmin) {
          // Other participant is the admin → we're the user → show just "Admin Support"
          return "Admin Support";
        }
        // Other participant is the user who requested support → we're the admin → show just user's name
        let userInfo = memberInfo || chat.participantInfo?.[otherUserId];
        if (userInfo) {
          const firstName = userInfo.first_name || userInfo.name || "";
          const suffix = userInfo.suffix || "";
          const specialName = userInfo.special_name || "";
          if (suffix) {
            const suffixUpper = suffix.trim().toUpperCase();
            const suffixWithPeriod =
              suffixUpper === "SR" || suffixUpper === "JR" ? `${suffix.trim()}.` : suffix.trim();
            return `${firstName}, ${suffixWithPeriod}`;
          }
          if (specialName) return `${firstName} ${specialName}`.trim();
          return (firstName || userInfo.name || "User").trim() || "User";
        }
        // Fall through to use lastMessage / messages for name
      }
    } catch {
      // ignore
    }

    // First, try to use memberInfo state
    let sourceMemberInfo = memberInfo;

    // Fallback 1: Check chat.participantInfo if memberInfo state is not yet populated
    if (!sourceMemberInfo && chat.participantInfo && chat.participantInfo[otherUserId]) {
      sourceMemberInfo = chat.participantInfo[otherUserId];
    }

    if (sourceMemberInfo) {
      const firstName = sourceMemberInfo.first_name || "";
      const suffix = sourceMemberInfo.suffix || "";
      const specialName = sourceMemberInfo.special_name || "";

      if (suffix) {
        const suffixUpper = suffix.trim().toUpperCase();
        const suffixWithPeriod =
          suffixUpper === "SR" || suffixUpper === "JR"
            ? `${suffix.trim()}.`
            : suffix.trim();
        return `${firstName}, ${suffixWithPeriod}`;
      } else if (specialName) {
        return `${firstName} ${specialName}`;
      }
      return firstName || sourceMemberInfo.name || "Unknown";
    }

    // Fallback 2: Try to get name from lastMessage or messages
    if (otherUserId) {
      // Check if lastMessage has senderName from the other participant
      if (chat.lastMessage?.senderName && chat.lastMessage?.senderId === otherUserId) {
        return chat.lastMessage.senderName;
      }
      
      // Check messages for senderName from the other participant
      const otherParticipantMessage = messages.find(
        (msg) => msg.senderId === otherUserId && msg.senderName
      );
      if (otherParticipantMessage?.senderName) {
        return otherParticipantMessage.senderName;
      }
    }

    return "Loading...";
  };

  const getHeadOfLineName = (member) => {
    if (!member) return "";
    const name = formatDashboardFamilyLineHeadName(member);
    return name || "Start/Top of Line";
  };

  // Get participant info for display
  const getParticipantInfo = () => {
    if (chat.type === "group") {
      return null;
    }

    const otherUserId = getOtherParticipantId(chat, currentUserId);
    if (!otherUserId) return null;

    // Don't show color/line for Admin Support chat
    try {
      const adminDetails = getAdminDetailsFromStorage();
      const adminId = getAdminUserIdFromDetails(adminDetails);
      if (adminId && String(adminId) === String(otherUserId)) {
        return null;
      }
    } catch {
      // ignore
    }

    // First, try to use memberInfo state (from API or chat.participantInfo)
    let sourceMemberInfo = memberInfo;

    // Fallback: Check chat.participantInfo directly if memberInfo state is not yet populated
    if (!sourceMemberInfo && chat.participantInfo && chat.participantInfo[otherUserId]) {
      sourceMemberInfo = chat.participantInfo[otherUserId];
    }

    if (sourceMemberInfo) {
      const headOfLineName = getHeadOfLineName(sourceMemberInfo);
      return {
        colorName: sourceMemberInfo.color_name || "",
        hexCode: sourceMemberInfo.hex_code || "#000000",
        generation: sourceMemberInfo.generation_level || "",
        headOfLineName: headOfLineName || "Start/Top of Line",
      };
    }

    return null;
  };

  if (!chat) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999",
        }}
      >
        Select a chat to start messaging
      </div>
    );
  }

  // Format member name
  const formatMemberName = (member) => {
    if (!member) return "";
    const firstName = member.first_name || "";
    const suffix = member.suffix || "";
    const specialName = member.special_name || "";

    if (suffix) {
      const suffixUpper = suffix.trim().toUpperCase();
      const suffixWithPeriod =
        suffixUpper === "SR" || suffixUpper === "JR"
          ? `${suffix.trim()}.`
          : suffix.trim();
      return `${firstName}, ${suffixWithPeriod}`;
    } else if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName || member.name || "";
  };

  // Who blocked the current user (for "You have been blocked by X" message)
  const blockedByName = (() => {
    if (!isBlocked) return "";
    if (isOneToOneChat && myBlockerUserId) {
      const info =
        chat.participantInfo?.[myBlockerUserId] ||
        (myBlockerUserId === otherUserId ? memberInfo : null);
      return formatMemberName(info || { first_name: "User", name: "User" });
    }
    if (chat?.type === "group" && chat?.createdBy) {
      const info = chat.participantInfo?.[chat.createdBy];
      return formatMemberName(info || { first_name: "Group admin", name: "Group admin" });
    }
    return isOneToOneChat ? "the other user" : "the group admin";
  })();

  // Get all group members with full info
  const getAllGroupMembers = () => {
    if (chat.type !== "group" || !chat.participants || chat.participants.length === 0) {
      return [];
    }

    // Get member info from both groupMembers state and chat.participantInfo
    return chat.participants
      .filter((userId) => userId !== currentUserId) // Exclude current user
      .map((userId) => {
        // First try to get from groupMembers state (from API)
        let member = groupMembers.find((m) => m.user_id === userId);
        
        // Fallback to chat.participantInfo if not found in groupMembers
        if (!member && chat.participantInfo && chat.participantInfo[userId]) {
          member = chat.participantInfo[userId];
        }
        
        if (member) {
          return member;
        }
        
        return null;
      })
      .filter((member) => member !== null); // Filter out null entries
  };

  // Get sender display name for group chat messages (for the tag above each message)
  const getSenderDisplayNameForGroup = (message) => {
    if (!message?.senderId) return "";
    if (message.senderId === currentUserId) {
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      return formatMemberName(localUser) || "You";
    }
    if (message.senderName) return message.senderName;
    const member = groupMembers.find((m) => m.user_id === message.senderId) ||
      (chat.participantInfo && chat.participantInfo[message.senderId]);
    return member ? formatMemberName(member) : "Unknown";
  };

  // Get sender name + hex color for group message tag (for UI accent)
  const getSenderTagInfoForGroup = (message) => {
    if (!message?.senderId || message.senderId === currentUserId) return null;
    const member = groupMembers.find((m) => m.user_id === message.senderId) ||
      (chat.participantInfo && chat.participantInfo[message.senderId]);
    const displayName = message.senderName || (member ? formatMemberName(member) : "Unknown");
    const hexCode = member?.hex_code || "#6b7280";
    const useColor = hexCode && hexCode !== "#000000";
    return { displayName, hexCode: useColor ? hexCode : "#6b7280" };
  };

  const getGroupMembersMeta = () => {
    const allMembers = getAllGroupMembers();
    return {
      totalCount: allMembers.length,
      allMembers,
    };
  };

  const participantName = getParticipantName();
  const participantInfo = getParticipantInfo();
  const groupMembersData = getGroupMembersMeta();
  const totalMembersCount = groupMembersData.totalCount || 0;
  const allGroupMembers = groupMembersData.allMembers || [];

  const mentionMembers = (() => {
    if (!chat) return [];
    const members = [];

    const addMember = (userId, info) => {
      if (!userId || String(userId) === String(currentUserId)) return;
      const name = info ? formatMemberName(info) : "Member";
      if (!name || members.some((m) => String(m.id) === String(userId))) return;
      members.push({
        id: userId,
        name,
        hexCode: info?.hex_code || "#6b7280",
      });
    };

    if (chat.type === "group" && chat.participants) {
      chat.participants.forEach((userId) => {
        const info =
          groupMembers.find((m) => String(m.user_id) === String(userId)) ||
          chat.participantInfo?.[userId];
        addMember(userId, info);
      });
    } else if (otherUserId) {
      const info = memberInfo || chat.participantInfo?.[otherUserId];
      addMember(otherUserId, info);
    }

    return members.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  })();

  const messagePlaceholder = editingMessageId
    ? "Edit your message"
    : chat.type === "group"
      ? `Message ${chat.groupName || "group"}`
      : `Message ${participantName}`;

  // Filter messages by search query
  const filteredMessages = messageSearchQuery.trim()
    ? messages.filter((m) =>
        m.text && m.text.toLowerCase().includes(messageSearchQuery.trim().toLowerCase())
      )
    : messages;

  // Sort members alphabetically by name for the modal
  const allGroupMembersSorted = [...allGroupMembers].sort((a, b) =>
    formatMemberName(a).localeCompare(formatMemberName(b), undefined, { sensitivity: "base" })
  );

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div className={`messages-chat-header${onBack ? " messages-chat-header--with-back" : ""}`}>
        <div className="messages-chat-header-top">
          {onBack && (
            <button
              type="button"
              className="messages-chat-header-back"
              onClick={onBack}
              aria-label="Back to conversations"
            >
              ←
            </button>
          )}
          <h3 className="messages-chat-header-name">
            <span className="messages-chat-header-name-text">{participantName}</span>
            {chat.type === "individual" &&
              participantInfo &&
              participantInfo.colorName &&
              participantInfo.hexCode && (
                <span className="messages-chat-header-line">
                  {renderColorDotAndName(
                    participantInfo.hexCode,
                    `${participantInfo.colorName} Line`
                  )}
                </span>
              )}
            {isOneToOneChat && iBlockedOtherUser && (
              <span className="messages-chat-header-blocked">BLOCKED</span>
            )}
          </h3>
          <div className="messages-chat-header-menu" style={{ position: "relative", flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setHeaderMenuOpen((prev) => !prev)}
              className="messages-chat-header-menu-btn"
              aria-label="Chat options"
            >
              ⋮
            </button>
            {headerMenuOpen && (
              <>
                <div
                  style={{ position: "fixed", inset: 0, zIndex: 9 }}
                  onClick={() => setHeaderMenuOpen(false)}
                  aria-hidden="true"
                />
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "100%",
                    marginTop: "4px",
                    backgroundColor: "#fff",
                    border: "1px solid #e8e8e8",
                    borderRadius: "8px",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
                    zIndex: 10,
                    minWidth: "180px",
                    padding: "2px 0",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteMode(true);
                      setSelectedMessageIds(new Set());
                      setHeaderMenuOpen(false);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "4px 14px",
                      border: "none",
                      background: "none",
                      fontSize: "14px",
                      lineHeight: 1.25,
                      color: "#333",
                      textAlign: "left",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Delete messages
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAllMessages}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "4px 14px",
                      border: "none",
                      background: "none",
                      fontSize: "14px",
                      lineHeight: 1.25,
                      color: "#d32f2f",
                      textAlign: "left",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Delete all messages
                  </button>
                  {isOneToOneChat && otherUserId && iBlockedOtherUser && (
                    <button
                      type="button"
                      onClick={handleUnblockUser}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "4px 14px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        lineHeight: 1.25,
                        color: "#333",
                        textAlign: "left",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Unblock user
                    </button>
                  )}
                  {isOneToOneChat && otherUserId && !iBlockedOtherUser && (
                    <button
                      type="button"
                      onClick={handleBlockUser}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "4px 14px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        lineHeight: 1.25,
                        color: "#333",
                        textAlign: "left",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Block user
                    </button>
                  )}
                  {chat?.type === "group" && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.openEditGroupModal) window.openEditGroupModal(chat);
                        setHeaderMenuOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "4px 14px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        lineHeight: 1.25,
                        color: "#333",
                        textAlign: "left",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Add/Remove members
                    </button>
                  )}
                  {chat?.type === "group" && isGroupCreator && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowBlockMemberModal(true);
                        setHeaderMenuOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "4px 14px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        lineHeight: 1.25,
                        color: "#333",
                        textAlign: "left",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Block member
                    </button>
                  )}
                  {chat?.type === "group" && isGroupCreator && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowUnblockMemberModal(true);
                        setHeaderMenuOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "4px 14px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        lineHeight: 1.25,
                        color: "#333",
                        textAlign: "left",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Unblock member
                    </button>
                  )}
                  {chat?.type === "group" && (
                    <button
                      type="button"
                      onClick={handleLeaveGroup}
                      disabled={leaving}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "4px 14px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        lineHeight: 1.25,
                        color: "#d32f2f",
                        textAlign: "left",
                        cursor: leaving ? "wait" : "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {leaving ? "Leaving…" : "Leave group"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="messages-chat-header-sub">
          {onExpandChatList && chatListCollapsed && (
            <button
              type="button"
              className="messages-chat-list-collapse-btn"
              onClick={onExpandChatList}
              title="Show conversations"
              aria-expanded="false"
              aria-controls="messages-chat-list-panel"
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} style={{ color: "#fff" }} />
            </button>
          )}
          {chat.type === "group" && (
            <button
              type="button"
              className="messages-chat-header-members"
              onClick={() => setShowMembersModal(true)}
            >
              Show list ({totalMembersCount})
            </button>
          )}
          <div className="messages-chat-header-search">
            <input
              type="text"
              value={messageSearchQuery}
              onChange={(e) => setMessageSearchQuery(e.target.value)}
              placeholder="Search messages..."
              className="messages-chat-header-search-input"
            />
            {messageSearchQuery && (
              <button
                type="button"
                className="messages-chat-header-search-clear"
                onClick={() => setMessageSearchQuery("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {deleteMode && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 20px",
            backgroundColor: "#fff3e0",
            borderBottom: "1px solid #e8e8e8",
            gap: "8px",
          }}
        >
          <span style={{ fontSize: "14px", color: "#333" }}>
            Select your messages to delete
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => {
                setDeleteMode(false);
                setSelectedMessageIds(new Set());
              }}
              style={{
                padding: "6px 12px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                background: "#fff",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteSelectedMessages}
              disabled={selectedMessageIds.size === 0 || deletingMessages}
              style={{
                padding: "6px 12px",
                border: "none",
                borderRadius: "6px",
                background: selectedMessageIds.size > 0 ? "#d32f2f" : "#ccc",
                color: "#fff",
                fontSize: "14px",
                cursor: selectedMessageIds.size > 0 && !deletingMessages ? "pointer" : "not-allowed",
              }}
            >
              {deletingMessages ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "16px 8px",
          backgroundColor: "#f9f9f9",
          minHeight: 0, // Important for flex scrolling
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={24} />
          </div>
        ) : messages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              padding: "40px 20px",
              fontSize: "16px",
            }}
          >
            No messages yet. Start the conversation!
          </div>
        ) : (
          <>
            {/* Date Label */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <span
                style={{
                  backgroundColor: "#e8e8e8",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                Today
              </span>
            </div>

            {/* Messages - WhatsApp style: name and timestamp inside bubble */}
            {messageSearchQuery.trim() && filteredMessages.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#999",
                  padding: "24px 20px",
                  fontSize: "15px",
                }}
              >
                No messages match your search.
              </div>
            ) : (
              <>
                {filteredMessages.map((message) => {
              const isSent = message.senderId === currentUserId;
              const showSenderNameInBubble = chat.type === "group" && !isSent;
              const senderTagInfo = showSenderNameInBubble ? getSenderTagInfoForGroup(message) : null;
              const menuOpen = messageMenuOpenId === message.id;

              if (message.type === "system") {
                return (
                  <div
                    key={message.id}
                    style={{
                      textAlign: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999",
                        fontStyle: "italic",
                      }}
                    >
                      {message.text}
                    </span>
                  </div>
                );
              }

              if (message.deleted) {
                return (
                  <div
                    key={message.id}
                    style={{
                      display: "flex",
                      justifyContent: isSent ? "flex-end" : "flex-start",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 14px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "12px",
                        border: "1px solid #e8e8e8",
                      }}
                    >
                      <span style={{ fontSize: "17px", lineHeight: 1 }} role="img" aria-label="deleted">🗑</span>
                      <span style={{ fontSize: "15px", color: "#999" }}>
                        This message was deleted.
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={message.id}
                  style={{
                    display: "flex",
                    justifyContent: isSent ? "flex-end" : "flex-start",
                    marginBottom: "16px",
                    maxWidth: "100%",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "98%",
                      minWidth: 0,
                      width: "fit-content",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isSent ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "6px",
                        minWidth: 0,
                        maxWidth: "100%",
                        flexDirection: isSent ? "row-reverse" : "row",
                      }}
                    >
                      {deleteMode && isSent && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedMessageIds.has(message.id)}
                            onChange={() => handleToggleSelectMessage(message.id)}
                            style={{ width: "18px", height: "18px" }}
                          />
                        </label>
                      )}
                      {isSent && !deleteMode && (
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => setMessageMenuOpenId((prev) => (prev === message.id ? null : message.id))}
                            style={{
                              padding: "4px 6px",
                              border: "none",
                              background: "transparent",
                              color: "#666",
                              cursor: "pointer",
                              borderRadius: "4px",
                              fontSize: "18px",
                              lineHeight: 1,
                            }}
                            aria-label="Message options"
                          >
                            ⋮
                          </button>
                          {menuOpen && (
                            <>
                              <div
                                style={{
                                  position: "fixed",
                                  inset: 0,
                                  zIndex: 9,
                                }}
                                onClick={() => setMessageMenuOpenId(null)}
                                aria-hidden="true"
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: "100%",
                                  marginTop: "2px",
                                  backgroundColor: "#fff",
                                  border: "1px solid #e8e8e8",
                                  borderRadius: "8px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                  zIndex: 10,
                                  minWidth: "80px",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => handleEditMessage(message)}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "8px 14px",
                                    border: "none",
                                    background: "none",
                                    fontSize: "14px",
                                    color: "#333",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSingleMessage(message.id)}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "8px 14px",
                                    border: "none",
                                    background: "none",
                                    fontSize: "14px",
                                    color: "#d32f2f",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      <div
                        style={{
                          backgroundColor: isSent ? "#025FEB" : "#fff",
                          color: isSent ? "#fff" : "#333",
                          padding: "11px 15px",
                          borderRadius: "12px",
                        border: isSent ? "none" : "1px solid #e8e8e8",
                        fontSize: "18px",
                        lineHeight: "1.2",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                        {senderTagInfo && (
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              marginBottom: "4px",
                              color: senderTagInfo.hexCode !== "#6b7280" ? senderTagInfo.hexCode : "#333",
                            }}
                          >
                            {senderTagInfo.displayName}
                          </div>
                        )}
                        <div style={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                          {message.type === "image" || message.type === "file" ? (
                            <MessageAttachment message={message} isSent={isSent} />
                          ) : (
                            <MessageText text={message.text} isSent={isSent} />
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#999",
                        marginTop: "4px",
                        padding: "0 4px",
                        alignSelf: "flex-end",
                        textAlign: "right",
                      }}
                    >
                      {formatMessageTime(message.timestamp)}
                      {message.edited && (
                        <span style={{ marginLeft: "6px", fontStyle: "italic" }}>(edited)</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
                <div ref={messagesEndRef} />
              </>
            )}
          </>
        )}
      </div>

      {/* Message Input or Blocked message */}
      {isBlocked ? (
        <div
          style={{
            padding: "20px",
            borderTop: "1px solid #e8e8e8",
            backgroundColor: "#fff",
            textAlign: "center",
            color: "#666",
            fontSize: "15px",
          }}
        >
          You have been blocked by {blockedByName}.
        </div>
      ) : (
      <form
        onSubmit={handleSendMessage}
        className="message-composer-form"
      >
        <MessageComposer
          ref={messageInputRef}
          value={messageText}
          onChange={setMessageText}
          onSend={handleSendMessage}
          disabled={sending}
          sending={sending}
          uploadingFile={uploadingFile}
          onFileUpload={handleFileUpload}
          placeholder={messagePlaceholder}
          mentionMembers={mentionMembers}
          editingMessageId={editingMessageId}
          onCancelEdit={() => {
            setEditingMessageId(null);
            setMessageText("");
            setMessageMenuOpenId(null);
          }}
        />
      </form>
      )}

      {/* Block member modal (group creator only) - same UI as Show list */}
      <ReactModal
        isOpen={showBlockMemberModal}
        onRequestClose={() => {
          setShowBlockMemberModal(false);
          setBlockMemberSearchQuery("");
        }}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "relative",
            inset: "auto",
            padding: "0",
            border: "none",
            background: "#fff",
            borderRadius: "8px",
            maxWidth: "400px",
            width: "65%",
            maxHeight: "60vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
        ariaHideApp={false}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e8", position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#333", lineHeight: "1" }}>
              Block a member ({allGroupMembers.filter((m) => m.user_id !== currentUserId && !(chat?.blockedMembers || []).includes(m.user_id)).length})
            </h2>
          </div>
          <button
            onClick={() => {
              setShowBlockMemberModal(false);
              setBlockMemberSearchQuery("");
            }}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
              padding: "0",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e8" }}>
          <input
            type="text"
            value={blockMemberSearchQuery}
            onChange={(e) => setBlockMemberSearchQuery(e.target.value)}
            placeholder="Search members..."
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              borderSpacing: "0",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "2px 8px 4px 8px",
                    textAlign: "left",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#333",
                    lineHeight: "1.2",
                  }}
                >
                  Message to
                </th>
                <th
                  style={{
                    padding: "2px 8px 4px 8px",
                    textAlign: "right",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#333",
                    lineHeight: "1.2",
                    width: "48px",
                  }}
                >
                  Block
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const blockableMembers = allGroupMembers.filter(
                  (m) => m.user_id !== currentUserId && !(chat?.blockedMembers || []).includes(m.user_id)
                );
                const filtered = blockMemberSearchQuery.trim()
                  ? blockableMembers.filter((m) =>
                      formatMemberName(m).toLowerCase().includes(blockMemberSearchQuery.trim().toLowerCase())
                    )
                  : blockableMembers;
                return filtered.length > 0 ? (
                  filtered.map((member, index) => (
                    <tr key={member.user_id || index} style={{ lineHeight: "1.2" }}>
                      <td
                        style={{
                          padding: "3px 8px",
                          fontSize: "14px",
                          lineHeight: "1.2",
                          color: (member.hex_code && member.hex_code !== "#000000") ? member.hex_code : "#333",
                        }}
                      >
                        {formatMemberName(member)}
                      </td>
                      <td
                        style={{
                          padding: "3px 8px",
                          textAlign: "right",
                          width: "48px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleBlockMember(member.user_id)}
                          title="Block this member"
                          style={{
                            padding: "4px 6px",
                            border: "1px solid #d32f2f",
                            borderRadius: "6px",
                            background: "#fff",
                            color: "#d32f2f",
                            fontSize: "14px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span role="img" aria-label="block">🚫</span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="2"
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#999",
                        lineHeight: "1",
                      }}
                    >
                      {blockableMembers.length === 0
                        ? "No other members to block."
                        : "No members match your search."}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </ReactModal>

      {/* Unblock member modal (group creator only) */}
      <ReactModal
        isOpen={showUnblockMemberModal}
        onRequestClose={() => {
          setShowUnblockMemberModal(false);
          setUnblockMemberSearchQuery("");
        }}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "relative",
            inset: "auto",
            padding: "0",
            border: "none",
            background: "#fff",
            borderRadius: "8px",
            maxWidth: "400px",
            width: "65%",
            maxHeight: "60vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
        ariaHideApp={false}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e8", position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#333", lineHeight: "1" }}>
              Unblock a member ({(chat?.blockedMembers || []).length})
            </h2>
          </div>
          <button
            onClick={() => {
              setShowUnblockMemberModal(false);
              setUnblockMemberSearchQuery("");
            }}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
              padding: "0",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e8" }}>
          <input
            type="text"
            value={unblockMemberSearchQuery}
            onChange={(e) => setUnblockMemberSearchQuery(e.target.value)}
            placeholder="Search blocked members..."
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              borderSpacing: "0",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "2px 8px 4px 8px",
                    textAlign: "left",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#333",
                    lineHeight: "1.2",
                  }}
                >
                  Member
                </th>
                <th
                  style={{
                    padding: "2px 8px 4px 8px",
                    textAlign: "right",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#333",
                    lineHeight: "1.2",
                    width: "88px",
                  }}
                >
                  Unblock
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const blockedIds = chat?.blockedMembers || [];
                const rows = blockedIds.map((userId) => {
                  const info =
                    chat?.participantInfo?.[userId] ||
                    groupMembers.find((m) => String(m.user_id) === String(userId));
                  const name = info ? formatMemberName(info) : "Member";
                  return { userId, name, hex: info?.hex_code || "#333" };
                });
                const filtered = unblockMemberSearchQuery.trim()
                  ? rows.filter((r) =>
                      r.name.toLowerCase().includes(unblockMemberSearchQuery.trim().toLowerCase())
                    )
                  : rows;
                if (blockedIds.length === 0) {
                  return (
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          padding: "24px",
                          textAlign: "center",
                          color: "#999",
                          fontSize: "14px",
                        }}
                      >
                        No blocked members in this group.
                      </td>
                    </tr>
                  );
                }
                return filtered.length > 0 ? (
                  filtered.map((row) => (
                    <tr key={row.userId} style={{ lineHeight: "1.2" }}>
                      <td
                        style={{
                          padding: "3px 8px",
                          fontSize: "14px",
                          lineHeight: "1.2",
                          color: row.hex && row.hex !== "#000000" ? row.hex : "#333",
                        }}
                      >
                        {row.name}
                      </td>
                      <td
                        style={{
                          padding: "3px 8px",
                          textAlign: "right",
                          width: "88px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleUnblockMember(row.userId)}
                          title="Unblock this member"
                          style={{
                            padding: "6px 10px",
                            border: "1px solid #2e7d32",
                            borderRadius: "6px",
                            background: "#fff",
                            color: "#2e7d32",
                            fontSize: "13px",
                            cursor: "pointer",
                          }}
                        >
                          Unblock
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="2"
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#999",
                      }}
                    >
                      No members match your search.
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </ReactModal>

      {/* Group Members Modal */}
      <ReactModal
        isOpen={showMembersModal}
        onRequestClose={() => setShowMembersModal(false)}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "relative",
            inset: "auto",
            padding: "0",
            border: "none",
            background: "#fff",
            borderRadius: "8px",
            maxWidth: "400px",
            width: "65%",
            maxHeight: "60vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
        ariaHideApp={false}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8e8e8", position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#333", lineHeight: "1" }}>
              {chat.groupName || "Group"} Members ({totalMembersCount})
            </h2>
          </div>
          <button
            onClick={() => setShowMembersModal(false)}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
              padding: "0",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              borderSpacing: "0",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "4px 8px 8px 8px",
                    textAlign: "left",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#333",
                    lineHeight: "1",
                  }}
                >
                  Message to
                </th>
              </tr>
            </thead>
            <tbody>
              {allGroupMembersSorted.length > 0 ? (
                allGroupMembersSorted.map((member, index) => (
                  <tr
                    key={member.user_id || index}
                  >
                    <td
                      style={{
                        padding: "0px 8px",
                        fontSize: "14px",
                        color: "#333",
                        lineHeight: "1",
                      }}
                    >
                      {formatMemberName(member)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="1"
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#999",
                      lineHeight: "1",
                    }}
                  >
                    No members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ReactModal>
    </div>
  );
};

export default ChatView;
